import { FC, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ensureArray,
  H1,
  Link,
  makeStyles,
  PageTitle,
  PasswordField,
  Tooltip,
} from '@perk-ui/core'
import { useFormik } from 'formik'
import { KeratinError } from 'keratin-authn/dist/types'
import { HelpCircle } from 'react-feather'
import { useRouteMatch } from 'react-router'
import * as yup from 'yup'

import AppLink from '../../../components/AppLink'
import SideBannerContainer from '../../../components/SideBannerContainer'
import apm from '../../../config/analytics'
import { useAuth } from '../../../features/auth/AuthContext'
import AuthenticationBase from '../AuthenticationBase'
import AuthenticationBanner from '../components/AuthenticationBanner'

interface ActivateAccountProps {}

export interface ActivateAccountLocationParams {
  token: string
}

interface ActivateAccountFormValues {
  password: string
  passwordConfirmation: string
  token?: string
}

const COUNTDOWN_SECONDS = 3

const useStyles = makeStyles((theme) => ({
  helperTooltip: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginLeft: theme.spacing(1),
      width: '18px',
      height: '18px',
      color: theme.palette.primary.main,
    },
  },
}))

const ActivateAccount: FC<ActivateAccountProps> = () => {
  const { resetPassword, initialize } = useAuth()
  const classes = useStyles()
  const [ticker, setTicker] = useState<number>(COUNTDOWN_SECONDS)
  const [didReset, setDidReset] = useState(false)
  const [keratinResponseError, setKeratinResponseError] = useState<
    KeratinError[] | null
  >(null)

  const match = useRouteMatch<ActivateAccountLocationParams>()
  const token = match.params.token

  const startTicking = () => {
    const tId = window.setInterval(() => {
      setTicker((tick) => {
        if (tick <= 1) {
          window.clearInterval(tId)
        }

        return tick - 1
      })
    }, 1000)
  }

  const loginValidations = yup.object({
    password: yup
      .string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/(?=.*[0-9])/, 'Password must have a number')
      .matches(/(?=.*[^A-Za-z0-9])/, 'Password must have a symbol'),
    passwordConfirmation: yup
      .string()
      .required('Password confirmation is required')
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  })

  const {
    values,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldError,
    isSubmitting,
    isValid,
    touched,
  } = useFormik<ActivateAccountFormValues>({
    enableReinitialize: true,
    initialValues: {
      password: '',
      passwordConfirmation: '',
    },
    validationSchema: loginValidations,
    onSubmit: (values) => {
      const { password } = values
      resetPassword({ password, token })
        .then((newToken) => {
          if (!newToken) {
            // Reset failed for an unknown reason. Bail
            setFieldError(
              'password',
              'Failed to activate account. Token invalid.',
            )
          } else {
            setDidReset(true)
            startTicking()

            setTimeout(() => {
              initialize()
            }, COUNTDOWN_SECONDS * 1000)
          }
        })
        .catch((error) => {
          apm.captureError(error)
          const keratinErrors = error as KeratinError[]

          const hasInsecurePassword = keratinErrors.some(
            (e) => e.field === 'password' && e.message === 'INSECURE',
          )
          if (hasInsecurePassword) {
            setFieldError(
              'password',
              'That is a common password. Please try something more unique.',
            )
          }

          const hasTokenIssue = keratinErrors.some((e) => e.field === 'token')
          if (hasTokenIssue) {
            setFieldError(
              'token',
              'It looks like your account activation link has expired. Please click below to get a new one.',
            )
          }
          setKeratinResponseError(keratinErrors)
          return
        })
    },
  })

  return (
    <SideBannerContainer
      bannerWidth={5}
      bannerSide="left"
      banner={<AuthenticationBanner />}
    >
      <PageTitle title="Activate Account" />
      <AuthenticationBase>
        <Box className="form-header">
          <H1>Activate Account</H1>
        </Box>
        <form className="form-content" onSubmit={handleSubmit}>
          {didReset && (
            <Alert severity="success" fade grow>
              Account Activated!
              <br />
              Redirecting you in {ticker} seconds.
            </Alert>
          )}
          {errors.token && (
            <Alert severity="error" fade grow>
              {errors.token}
              <br />
              <br />
              <AppLink to="/resend-activation-link-new">
                Request a new link
              </AppLink>
            </Alert>
          )}
          {keratinResponseError && (
            <Alert severity="error" fade grow>
              Account activation failed. Please send the below information or a
              screenshot to{' '}
              <Link
                href="mailto:support@databiologics.com"
                underline="hover"
                target="_blank"
                rel="noopener noreferrer"
              >
                support@databiologics.com
              </Link>
              <br />
              <br />
              {ensureArray(keratinResponseError).map((eo) => {
                return eo instanceof Error
                  ? eo.toString()
                  : JSON.stringify(eo, undefined, 4)
              })}
            </Alert>
          )}
          <PasswordField
            label={
              <span className={classes.helperTooltip}>
                Password{' '}
                <Tooltip title="Password must be at least 8 characters and include a symbol and a number">
                  <HelpCircle />
                </Tooltip>
              </span>
            }
            name="password"
            placeholder="Enter new password"
            fullWidth
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
          />
          <PasswordField
            label="Confirm Password"
            name="passwordConfirmation"
            placeholder="Confirm new password"
            fullWidth
            value={values.passwordConfirmation}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.passwordConfirmation &&
              Boolean(errors.passwordConfirmation)
            }
            helperText={
              touched.passwordConfirmation && errors.passwordConfirmation
            }
          />
          <Button
            type="submit"
            fullWidth
            loading={isSubmitting}
            disabled={!isValid}
          >
            Activate Account
          </Button>
        </form>
      </AuthenticationBase>
    </SideBannerContainer>
  )
}

export default ActivateAccount
